import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UNITS, fmtDate } from "./stockUtils";

const emptyProduct = {
  name: "",
  category: "chemical",
  brand: "",
  sku: "",
  unit: "un",
  min_quantity: 0,
  supplier: "",
  location: "",
  safety_sheet_url: "",
  shelf_life_days: "",
  unit_weight: "",
  unit_weight_unit: "g",
  notes: "",
};

const emptyLot = { quantity: "", production_date: "", shelf_life_days: "", batch: "", responsible: "" };

const today = () => new Date().toISOString().slice(0, 10);

const addDays = (date, days) => {
  if (!date || days === "" || days == null) return "";
  const d = new Date(date + "T00:00:00");
  d.setDate(d.getDate() + Number(days));
  return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};

// Cadastro / edição do PRODUTO (mestre). Na criação, permite o 1º lote junto.
export function ProductForm({ open, onOpenChange, product, categories, userName, onSaved }) {
  const editing = !!product;
  const [form, setForm] = useState(emptyProduct);
  const [lot, setLot] = useState(emptyLot);
  const [withLot, setWithLot] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (product) {
      setForm({ ...emptyProduct, ...product, shelf_life_days: product.shelf_life_days ?? "", unit_weight: product.unit_weight ?? "" });
    } else {
      setForm(emptyProduct);
      setLot({ ...emptyLot, production_date: today(), responsible: userName || "" });
      setWithLot(true);
    }
  }, [open, product, userName]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const lotExpiry = useMemo(
    () => addDays(lot.production_date, lot.shelf_life_days !== "" ? lot.shelf_life_days : form.shelf_life_days),
    [lot.production_date, lot.shelf_life_days, form.shelf_life_days]
  );

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Nome obrigatório");
      return;
    }
    const payload = {
      ...form,
      name: form.name.trim(),
      min_quantity: Number(form.min_quantity) || 0,
      shelf_life_days: form.shelf_life_days === "" ? null : Number(form.shelf_life_days),
      unit_weight: form.unit_weight === "" || form.unit_weight == null ? null : Number(form.unit_weight),
    };
    delete payload.lots;
    delete payload.id;
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.patch(`/sanitary/products/${product.id}`, payload);
        toast.success("Produto atualizado");
        onSaved?.(data, null);
      } else {
        if (withLot && Number(lot.quantity) > 0) {
          payload.initial_lot = {
            quantity: Number(lot.quantity),
            production_date: lot.production_date || null,
            shelf_life_days: lot.shelf_life_days === "" ? null : Number(lot.shelf_life_days),
            batch: lot.batch || null,
            responsible: lot.responsible || userName || null,
          };
        }
        const { data } = await api.post("/sanitary/products", payload);
        toast.success(data.created_lot ? "Produto cadastrado com o 1º lote — etiqueta gerada" : "Produto cadastrado");
        onSaved?.(data, data.created_lot || null);
      }
      onOpenChange(false);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha ao salvar produto");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="product-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{editing ? "Editar produto" : "Novo produto"}</DialogTitle>
          <DialogDescription className="font-body text-xs text-stone-500">
            O produto é o cadastro mestre. Cada entrada vira um <b>lote</b> com validade e etiqueta QR próprias; a
            saída segue a regra PVPS.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Nome *</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1 bg-white" placeholder="Ex: Molho de tomate caseiro" data-testid="product-name" autoFocus />
          </div>
          <div>
            <Label>Categoria</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger className="mt-1 bg-white" data-testid="product-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Unidade de estoque</Label>
            <Select value={form.unit} onValueChange={(v) => set("unit", v)}>
              <SelectTrigger className="mt-1 bg-white" data-testid="product-unit"><SelectValue /></SelectTrigger>
              <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Estoque mínimo (alerta)</Label>
            <Input type="number" step="0.01" value={form.min_quantity} onChange={(e) => set("min_quantity", e.target.value)} className="mt-1 bg-white" data-testid="product-min" />
            <p className="text-[11px] text-stone-500 mt-1 font-body">Compara com o saldo total (todos os lotes).</p>
          </div>
          <div>
            <Label>Validade padrão (dias após produção)</Label>
            <Input type="number" min="0" value={form.shelf_life_days} onChange={(e) => set("shelf_life_days", e.target.value)} className="mt-1 bg-white" placeholder="Ex: 3" data-testid="product-shelf-days" />
            <p className="text-[11px] text-stone-500 mt-1 font-body">Pré-preenche a validade de cada novo lote.</p>
          </div>
          <div>
            <Label>Marca</Label>
            <Input value={form.brand || ""} onChange={(e) => set("brand", e.target.value)} className="mt-1 bg-white" />
          </div>
          <div>
            <Label>SKU / Código</Label>
            <Input value={form.sku || ""} onChange={(e) => set("sku", e.target.value)} className="mt-1 bg-white" />
          </div>
          <div>
            <Label>Peso por unidade (etiqueta)</Label>
            <div className="flex gap-2 mt-1">
              <Input type="number" step="0.01" min="0" value={form.unit_weight} onChange={(e) => set("unit_weight", e.target.value)} placeholder="Ex: 250" className="bg-white" data-testid="product-unit-weight" />
              <Select value={form.unit_weight_unit || "g"} onValueChange={(v) => set("unit_weight_unit", v)}>
                <SelectTrigger className="w-20 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="g">g</SelectItem>
                  <SelectItem value="kg">kg</SelectItem>
                  <SelectItem value="ml">ml</SelectItem>
                  <SelectItem value="L">L</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Fornecedor</Label>
            <Input value={form.supplier || ""} onChange={(e) => set("supplier", e.target.value)} className="mt-1 bg-white" />
          </div>
          <div>
            <Label>Localização</Label>
            <Input value={form.location || ""} onChange={(e) => set("location", e.target.value)} placeholder="Depósito / câmara / sala" className="mt-1 bg-white" />
          </div>
          <div>
            <Label>FISPQ (URL ficha de segurança)</Label>
            <Input value={form.safety_sheet_url || ""} onChange={(e) => set("safety_sheet_url", e.target.value)} placeholder="https://…" className="mt-1 bg-white" />
          </div>
          <div className="col-span-2">
            <Label>Observações</Label>
            <Textarea rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} className="mt-1 bg-white" />
          </div>
        </div>

        {!editing && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 space-y-3" data-testid="product-initial-lot">
            <label className="flex items-center gap-2 text-sm font-body font-semibold text-emerald-900 cursor-pointer">
              <input type="checkbox" checked={withLot} onChange={(e) => setWithLot(e.target.checked)} data-testid="product-with-lot" />
              Já dar entrada no 1º lote agora
            </label>
            {withLot && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Quantidade ({form.unit})</Label>
                  <Input type="number" step="0.01" min="0" value={lot.quantity} onChange={(e) => setLot({ ...lot, quantity: e.target.value })} className="mt-1 bg-white" data-testid="product-lot-qty" />
                </div>
                <div>
                  <Label className="text-xs">Produção</Label>
                  <Input type="date" value={lot.production_date} onChange={(e) => setLot({ ...lot, production_date: e.target.value })} className="mt-1 bg-white" data-testid="product-lot-date" />
                </div>
                <div>
                  <Label className="text-xs">Validade (dias)</Label>
                  <Input type="number" min="0" value={lot.shelf_life_days} onChange={(e) => setLot({ ...lot, shelf_life_days: e.target.value })} placeholder={form.shelf_life_days !== "" ? `${form.shelf_life_days} (padrão)` : "—"} className="mt-1 bg-white" data-testid="product-lot-shelf" />
                </div>
                <div>
                  <Label className="text-xs">Nº do lote</Label>
                  <Input value={lot.batch} onChange={(e) => setLot({ ...lot, batch: e.target.value })} placeholder="opcional" className="mt-1 bg-white" data-testid="product-lot-batch" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Responsável pela produção</Label>
                  <Input value={lot.responsible} onChange={(e) => setLot({ ...lot, responsible: e.target.value })} placeholder="Nome de quem produziu / recebeu" className="mt-1 bg-white" data-testid="product-lot-responsible" />
                </div>
                <p className="col-span-full text-[11px] text-emerald-800 font-body">
                  {lotExpiry ? <>Validade calculada: <b>{fmtDate(lotExpiry)}</b></> : "Informe produção + dias para calcular a validade."}
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button onClick={save} disabled={saving} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="product-submit">
            {editing ? "Salvar alterações" : "Cadastrar produto"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
